import { apiClient, apiOrigin, type ApiResult, type RequestOptions } from './apiClient';
export const executivePriorityGrants = {
  read: 'dashboard:executive_priorities:read',
  create: 'dashboard:executive_priorities:create',
  update: 'dashboard:executive_priorities:update',
  archive: 'dashboard:executive_priorities:archive',
  complete: 'dashboard:executive_priorities:complete',
} as const;
export type PrioritySourceType =
  | 'Project'
  | 'ApprovalRequest'
  | 'AgentRecommendation'
  | 'DocumentAsset'
  | 'ArInvoice'
  | 'ApInvoice';
export interface PriorityInvoiceScope {
  sourceType: 'ArInvoice' | 'ApInvoice';
  sourceId?: string;
}
export interface PriorityOptionsQuery extends Partial<PriorityInvoiceScope> {
  q?: string;
  documentAssetId?: string;
  page?: number;
  pageSize?: number;
}
export interface PrioritySourceInput {
  type: PrioritySourceType;
  id: string;
  expectedVersion?: string;
}
export interface PriorityInput {
  title: string;
  description?: string | null;
  ownerId: string;
  dueAt: string;
  severity: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
  completionCriteria: string;
  source?: PrioritySourceInput | null;
  evidenceDocumentAssetIds?: string[];
}
export type PriorityUpdate = Partial<PriorityInput> & {
  expectedVersion: number;
  status?: 'OPEN' | 'IN_PROGRESS';
};
export type PriorityView =
  | {
      id: string;
      version: number;
      redacted: true;
      sourceState: 'restricted' | 'missing';
      permissions: { canUpdate: false; canArchive: false; canComplete: false };
    }
  | {
      id: string;
      version: number;
      redacted: false;
      title: string;
      description: string | null;
      severity: string;
      status: string;
      owner: { id: string; name: string; title: string | null; active: boolean };
      dueAt: string;
      completionCriteria: string;
      sourceState: 'manual' | 'current' | 'stale';
      source: null | {
        type: PrioritySourceType;
        id: string;
        title: string;
        href: string;
        pinnedVersion: string;
        currentVersion: string;
        observedAt: string;
      };
      evidence: Array<{
        id: string;
        documentAssetId: string;
        documentVersion: string;
        documentSha256: string;
        title: string;
        href: string;
        state: 'current' | 'stale';
      }>;
      completedAt: string | null;
      completedById: string | null;
      completionNote: string | null;
      archivedAt: string | null;
      archivedById: string | null;
      createdAt: string;
      updatedAt: string;
      permissions: { canUpdate: boolean; canArchive: boolean; canComplete: boolean };
    };
export type VisiblePriority = Extract<PriorityView, { redacted: false }>;
export interface PriorityList {
  generatedAt: string;
  items: PriorityView[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
  permissions: {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canArchive: boolean;
    canComplete: boolean;
  };
  disclosure: string;
}
export interface PriorityOptions {
  sourceMeta?: { page: number; pageSize: number; total: number; totalPages: number };
  owners: Array<{ id: string; name: string; title: string | null }>;
  sources: Array<{
    type: PrioritySourceType;
    id: string;
    title: string;
    version: string;
    href: string;
  }>;
  evidence: Array<{ id: string; title: string; href: string }>;
  coverage: { ownerLimit: number; perSourceLimit: number; complete: boolean; reason: string };
}
const root = `${apiOrigin}/api/v1/dashboard/executive-priorities`;
async function required<T>(promise: Promise<ApiResult<T>>): Promise<T> {
  const result = await promise;
  if (!result.ok || result.data === null)
    throw new Error(result.error ?? 'Executive priorities are unavailable');
  return result.data;
}
export const executivePrioritiesClient = {
  list: (page: number, options?: RequestOptions, scope?: PriorityInvoiceScope, pageSize = 40) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (scope) {
      params.set('sourceType', scope.sourceType);
      if (scope.sourceId) params.set('sourceId', scope.sourceId);
    }
    return required(apiClient.get<PriorityList>(`${root}?${params}`, options));
  },
  detail: (id: string, options?: RequestOptions) =>
    required(apiClient.get<PriorityView>(`${root}/${id}`, options)),
  options: (query?: PriorityOptionsQuery, options?: RequestOptions) => {
    const params = new URLSearchParams();
    if (query?.q) params.set('q', query.q);
    if (query?.documentAssetId) params.set('documentAssetId', query.documentAssetId);
    if (query?.sourceType) params.set('sourceType', query.sourceType);
    if (query?.sourceId) params.set('sourceId', query.sourceId);
    if (query?.page) params.set('page', String(query.page));
    if (query?.pageSize) params.set('pageSize', String(query.pageSize));
    const encoded = params.toString();
    return required(
      apiClient.get<PriorityOptions>(`${root}/options${encoded ? `?${encoded}` : ''}`, options),
    );
  },
  create: (body: PriorityInput, options?: RequestOptions) =>
    required(apiClient.post<PriorityView>(root, body, options)),
  update: (id: string, body: PriorityUpdate, options?: RequestOptions) =>
    required(apiClient.patch<PriorityView>(`${root}/${id}`, body, options)),
  archive: (id: string, expectedVersion: number, options?: RequestOptions) =>
    required(apiClient.post<PriorityView>(`${root}/${id}/archive`, { expectedVersion }, options)),
  complete: (
    id: string,
    expectedVersion: number,
    completionNote: string,
    options?: RequestOptions,
  ) =>
    required(
      apiClient.post<PriorityView>(
        `${root}/${id}/complete`,
        { expectedVersion, completionNote },
        options,
      ),
    ),
};
